import {
  GuidedQuotingStatusTagStyle,
  IGuidedQuoting,
} from '@monetize/types/app';
import {
  AppColumnDef,
  createColumnHelper,
} from '../../../../components/Monetize/DataTableNew/appTable';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAtom } from 'jotai';
import {
  useDeleteEntity,
  useGetListData,
  usePerformEntityAction,
} from '../../../../api/queryUtils';
import {
  MBox,
  MButton,
  MFlex,
  MPageContainer,
  MSettingsPageHeader,
} from '../../../../components/Monetize';
import DataTableActions, {
  ActionType,
} from '../../../../components/Monetize/DataTable/MDataTableActions';
import { DataTable } from '../../../../components/Monetize/DataTableNew/DataTable';
import { DataTableRef } from '../../../../components/Monetize/DataTableNew/DataTableTypes';
import {
  ACTION_COLUMN_KEY,
  createActionColumn,
} from '../../../../components/Monetize/DataTableNew/dataTableNewRenderers';
import {
  dateTimeBodyTemplate,
  enumDisplayBodyTemplate,
  getColHeaderWithSize,
  textBodyTemplate,
} from '../../../../components/Monetize/DataTableNew/tableUtilsNew';
import { ROUTES } from '../../../../constants';
import { GuidedQuotingStatusDisplayText } from '../../../../constants/guidedQuoting';
import { getGuidedQuotingRoute } from '../../../../constants/routes';
import { useDocumentHead } from '../../../../services/documentHead';
import { tablePagerSelector } from '../../../../store/global.store';
import { FilterStateKeys } from '../../../../store/store.types';
import { navigateOrOpenNewWindow } from '../../../../utils/misc';

const columnHelper = createColumnHelper<IGuidedQuoting>();

const getColumns = (
  actionBodyTemplate: (rowData: IGuidedQuoting) => React.ReactNode,
): AppColumnDef<IGuidedQuoting, any>[] => [
  columnHelper.accessor('name', {
    ...getColHeaderWithSize('Name', 250),
    cell: textBodyTemplate(),
    enableSorting: true,
    sortFn: 'text',
  }),
  columnHelper.accessor('status', {
    ...getColHeaderWithSize('Status', 150),
    cell: enumDisplayBodyTemplate({
      displayMap: GuidedQuotingStatusDisplayText,
      styleMap: GuidedQuotingStatusTagStyle,
    }),
    enableSorting: true,
    sortFn: 'text',
  }),
  columnHelper.accessor('createDate', {
    ...getColHeaderWithSize('Created', 180),
    cell: dateTimeBodyTemplate(),
    enableSorting: true,
  }),
  createActionColumn<IGuidedQuoting>(actionBodyTemplate),
];

const GuidedQuoting = () => {
  const navigate = useNavigate();
  const { setDocTitle } = useDocumentHead();

  const [pager, setPager] = useAtom(
    tablePagerSelector(FilterStateKeys.GUIDED_QUOTING_LIST),
  );

  const redirectToCreate = () =>
    navigate(ROUTES.SETTINGS_GUIDED_QUOTING_CREATE);

  const { data: guidedQuotingList, isLoading } = useGetListData<IGuidedQuoting>(
    'guidedQuoting_v2',
    {
      config: pager,
    },
    { meta: { showErrorToast: true } },
  );

  const loading = isLoading;
  const tableRef = useRef<DataTableRef>(null);

  const { mutateAsync: doActivateDeactivate } =
    usePerformEntityAction<IGuidedQuoting>('guidedQuoting_v2', {
      endpointArgs: { skipNestedEndpoint: true },
      meta: { showErrorToast: true },
    });

  const { mutateAsync: doDeleteQuestionById } = useDeleteEntity<IGuidedQuoting>(
    'guidedQuoting_v2',
    {
      endpointArgs: { skipNestedEndpoint: true },
      meta: { showErrorToast: true },
    },
  );

  const actionBodyTemplate = useCallback(
    (rowData: IGuidedQuoting) => {
      const actions: ActionType[] = [
        {
          title: 'Activate',
          enabled: rowData.status === 'INACTIVE',
          confirmProps: {
            title: `Are you sure you want to activate this Guided Quoting configuration?`,
            description: 'All other configurations will be deactivated.',
            yesBtnProps: {
              variant: 'primary',
            },
          },
          action: async () => {
            doActivateDeactivate({
              id: rowData.id,
              action: 'activate',
            });
          },
        },
        {
          title: 'Deactivate',
          enabled: rowData.status === 'ACTIVE',
          color: 'danger',
          confirmProps: {
            title: `Are you sure you want to deactivate this Guided Quoting configuration?`,
            description:
              'You will no longer be able to create quotes using this configuration.',
          },
          action: () => {
            doActivateDeactivate({
              id: rowData.id,
              action: 'deactivate',
            });
          },
        },
        {
          title: 'Delete',
          enabled: rowData.status !== 'ACTIVE',
          color: 'danger',
          confirmProps: {
            title: `Are you sure you want to delete this Guided Quoting configuration?`,
            description: 'This action is not reversible.',
          },
          action: () => {
            doDeleteQuestionById({
              id: rowData.id,
            });
          },
        },
      ];

      return <DataTableActions actions={actions} />;
    },
    [doActivateDeactivate, doDeleteQuestionById],
  );

  const columns = useMemo(
    () => getColumns(actionBodyTemplate),
    [actionBodyTemplate],
  );

  useEffect(() => {
    setDocTitle('Settings', 'Guided Quoting');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <MPageContainer alignItems="stretch">
      <MSettingsPageHeader title="Guided Quoting">
        {guidedQuotingList?.content.length !== 0 && !loading && (
          <MFlex gap={4}>
            <MButton
              variant="primary"
              onClick={redirectToCreate}
              data-testid="new-approval-btn"
            >
              New
            </MButton>
          </MFlex>
        )}
      </MSettingsPageHeader>
      <MBox width="100%">
        <DataTable
          ref={tableRef}
          columns={columns}
          data={guidedQuotingList?.content ?? []}
          enablePagination={
            guidedQuotingList ? guidedQuotingList.totalElements > 20 : false
          }
          pinnedRightColumns={[ACTION_COLUMN_KEY]}
          totalElements={guidedQuotingList?.totalElements}
          totalPages={guidedQuotingList?.totalPages}
          pager={pager}
          setPager={setPager}
          isLoading={isLoading}
          emptyProps={{
            mainMessage:
              'Looks like there are no Guided Quoting configurations added yet.',
            btnLabel: 'New Guided Quoting Configuration',
            onClick: () => redirectToCreate(),
          }}
          onRowClick={(row, _tableRow, openInNewWindow) =>
            navigateOrOpenNewWindow(
              navigate,
              getGuidedQuotingRoute(row.id),
              openInNewWindow,
            )
          }
        />
      </MBox>
    </MPageContainer>
  );
};

export default GuidedQuoting;
